import {BaseElement, BASIS, Label, OSkinableInit} from "/@back@/commons/basis.js";
import {IItemUiEnv} from "/@back@/wsp/views/itemMain.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {IView} from "/@lib@/commons/views.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {WSP} from "/@lib@/wsp/wsp.js";
import {ImgSize, OImgSizeConfig} from "/@back@/wsp/widgets/imgSize.js";
import {SRC} from "/@lib@/wsp/src.js";
import {defaultRemoteResSelector, IRemoteResRendererActionCtx, OpenRemoteResAct, ORemoteResRendererInit, RemoteResRenderer} from "/@back@/core/widgets/remoteResRenderer.js";
import {JRemoteResDescFromClient, RemoteRes} from "/@lib@/core/remote.js";
import {IShortDescCtx} from "/@lib@/wsp/item.js";
import {IBasicUniversePointer} from "/@lib@/core/universe.js";
import {Action, EButtonUiContext} from "/@lib@/commons/actions.js";

/**
 * View du contenu d'un item via une iframe (pdf...).
 *
 * Config attributes:
 */
export interface OItemRemoteResInit extends OSkinableInit {
	reg?: IReg<IItemUiEnv>
	transform?: string,
	size: OImgSizeConfig,
	previewInit: ORemoteResRendererInit,
	remoteResSelector?: (reg: IReg<IBasicUniversePointer>, resDesc: JRemoteResDescFromClient) => RemoteRes | null
}

export interface ItemRemoteRes extends BaseElement {
	initialize(init: OItemRemoteResInit): this;
}

export class ItemRemoteRes extends BaseElement implements IView {
	reg: IReg<IItemUiEnv>;

	protected config: OItemRemoteResInit;

	remoteResRenderer?: RemoteResRenderer;

	remoteDesc?: JRemoteResDescFromClient | null;

	_initialize(init: OItemRemoteResInit) {
		this.reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin("wsp-item-remote-res", init);
		if (!init.remoteResSelector) init.remoteResSelector = defaultRemoteResSelector(init.previewInit?.forceWebRender);
		this.config = init;
		const { env } = this.reg;

		const renderer = this.remoteResRenderer = sr.appendChild(<RemoteResRenderer id="preview" î={BASIS.newInit(init.previewInit, init.reg)}/> as RemoteResRenderer);

		const self = this;
		ImgSize.addResView(init.size, "actions:wsp:itemRemoteRes", sr, renderer, this.reg, {
			shortDescs: env.srcView.shortDescs,
			reg: this.reg,
			emitter: env.srcView.emitter,
			infoBroker: env.infoBroker,
			get remoteResRenderer() {
				return self.remoteResRenderer;
			},
			get remoteDesc() {
				return self.remoteDesc;
			}
		} as IShortDescCtx & IRemoteResRendererActionCtx);

		this.reg.env.longDescChange?.add(() => {
			this.refresh();
		});
	}

	protected async _refresh(): Promise<void> {
		const { wsp, longDesc } = this.reg.env;
		let transform = "transform=facet&facet=remoteContent&outType=RESDESC";
		this.remoteDesc = (await WSP.fetchStreamJson<JRemoteResDescFromClient>(wsp, null, SRC.srcRef(longDesc), transform)) || {url: null, status: -1};
		await this.remoteResRenderer.setRemoteRes(this.remoteDesc ? this.config.remoteResSelector(this.reg, this.remoteDesc) : null);
		(this.shadowRoot.querySelector("wsp-imgsize,c-bar-actions") as BaseElement)?.refresh();
	}
}

REG.reg.registerSkin("wsp-item-remote-res", 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex: 1;
		flex-direction: column;
		padding: 1px;
	}
`);

customElements.define("wsp-item-remote-res", ItemRemoteRes);

export class ShowTitleAction<CTX extends IRemoteResRendererActionCtx> extends Action<CTX> {
	static SINGLETON = new ShowTitleAction();

	constructor() {
		super("showTitle");
	}

	buildCustomButton(ctx: CTX, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined {
		return new Label().initialize({ label: ctx.remoteDesc?.title, skinOver: "wsp-item-remote-res/showTitle"});
	}
}

REG.reg.registerSkin("wsp-item-remote-res/showTitle", 1, /* language=CSS */ `
	:host {
		align-items: center;
		flex: 1;
	}
`);


REG.reg.addToList("actions:wsp:itemRemoteRes", "open", REG.LEVELAUTH_CORE, OpenRemoteResAct.SINGLETON);
REG.reg.addToList("actions:wsp:itemRemoteRes", "showTitle", REG.LEVELAUTH_CORE, ShowTitleAction.SINGLETON, 100);
REG.reg.addToList("actions:wsp:itemRemoteRes", "downloadSrc", REG.LEVELAUTH_CORE, null);